<template>
  <div>
    <div class="appui">
      <div class="appui-head">
        <div>
          <h1 class="appui-title">Arquivos</h1>
          <div class="appui-crumb">App <span class="sep">/</span> Arquivos</div>
        </div>
      </div>

      <!-- Vazio: nenhum cadastrado -->
      <div v-if="!list.length" class="appui-emptystate">
        <div class="appui-emptystate-icon"><i class="bx bx-folder"></i></div>
        <div class="appui-emptystate-title">Nenhum arquivo ainda</div>
        <div class="appui-emptystate-sub">Disponibilize apresentações, PDFs e materiais para o participante baixar no app.</div>
        <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Adicionar o primeiro arquivo</button>
      </div>

      <!-- Bloco principal -->
      <section v-else class="appui-card appui-card--flush">
        <div class="appui-card-toolbar">
          <div class="appui-card-toolbar-left">
            <div class="appui-search">
              <i class="bx bx-search-alt"></i>
              <input v-model.trim="search" placeholder="Buscar por descrição...">
              <button v-if="search" class="appui-search-clear" @click="search = ''" title="Limpar busca"><i class="mdi mdi-close"></i></button>
            </div>
          </div>
          <div class="appui-card-toolbar-right">
            <button class="appui-btn appui-btn--primary" @click="openCreate()">+ Novo arquivo</button>
          </div>
        </div>

        <!-- Sem resultado de busca -->
        <div v-if="!filteredList.length" class="appui-inline-empty">
          Nenhum arquivo encontrado para os filtros. <a @click="clearFilters()">Limpar filtros</a>
        </div>

        <!-- Tabela (reordenação por arrastar) -->
        <table v-else class="appui-table">
          <thead>
            <tr>
              <th style="width:56px"></th>
              <th>Descrição</th>
              <th>Arquivo</th>
              <th>Status</th>
              <th style="text-align:right">Ações</th>
            </tr>
          </thead>
          <draggable
            v-model="displayList"
            :element="'tbody'"
            :options="{ animation: 180, handle: '.appui-grip', ghostClass: 'appui-drag-ghost', disabled: isFiltering }"
            @end="onReorder"
          >
            <tr v-for="a in displayList" :key="a.id">
              <td>
                <span class="appui-grip" :class="{ 'is-disabled': isFiltering }" :title="isFiltering ? 'Limpe a busca e o filtro para reordenar' : 'Arraste para reordenar'">⠿</span>
              </td>
              <td>
                <div style="display:flex;align-items:center;gap:11px">
                  <span class="appui-icon-chip" style="background:#eef1fb;color:#556ee6"><i class="mdi mdi-file-document-outline"></i></span>
                  <span style="font-weight:500;color:#495057">{{ a.description }}</span>
                </div>
              </td>
              <td>
                <a v-if="a.url_download" :href="a.url_download" target="_blank" rel="noopener" class="appui-tbtn appui-tbtn--primary" style="text-decoration:none"><i class="mdi mdi-download"></i> Abrir</a>
                <span v-else style="color:#adb5bd">—</span>
              </td>
              <td><span class="appui-badge" :class="a.status ? 'appui-badge--green' : 'appui-badge--muted'" role="button" @click="toggle(a)">{{ a.status ? 'Ativo' : 'Oculto' }}</span></td>
              <td style="text-align:right;white-space:nowrap">
                <button class="appui-tbtn" style="margin-right:6px" @click="openEdit(a)">Editar</button>
                <button class="appui-tbtn appui-tbtn--danger" @click="remove(a)">Excluir</button>
              </td>
            </tr>
          </draggable>
        </table>

        <!-- Rodapé: mostrar X por vez + contador -->
        <div v-if="filteredList.length" class="appui-card-footer">
          <div class="appui-perpage">
            <span>Mostrar</span>
            <select class="appui-select appui-select--sm" v-model.number="perPage">
              <option :value="20">20</option>
              <option :value="50">50</option>
              <option :value="100">100</option>
              <option :value="0">Todos</option>
            </select>
            <span>por vez</span>
          </div>
          <span class="appui-count">Mostrando {{ displayList.length }} de {{ filteredList.length }}</span>
        </div>
      </section>
    </div>

    <b-modal v-model="showModal" :title="form.id ? 'Editar arquivo' : 'Novo arquivo'" hide-footer>
      <form @submit.prevent="save()">
        <div class="form-group"><label>Descrição *</label><input type="text" class="form-control" v-model="form.description" required></div>
        <div class="form-group"><label>URL do arquivo *</label><input type="url" class="form-control" v-model="form.url_download" placeholder="https://..." required></div>
        <div class="text-right">
          <button type="button" class="btn btn-light mr-2" @click="showModal = false">Cancelar</button>
          <button type="submit" class="btn btn-primary" :disabled="saving">Salvar</button>
        </div>
      </form>
    </b-modal>
  </div>
</template>

<script>
import draggable from 'vuedraggable'
import { errorMessage, successMessage } from '@/components/alertsMessages'
import services from './services'
import './app-ui.css'

const empty = () => ({ id: null, description: '', url_download: '' })

export default {
  components: { draggable },
  name: 'app-archives',
  data () {
    return { list: [], showModal: false, saving: false, form: empty(), search: '', perPage: 50 }
  },
  mounted () { this.load() },
  computed: {
    isFiltering () { return !!this.search },
    filteredList () {
      if (!this.search) return this.list
      const q = this.search.toLowerCase()
      return this.list.filter(a => (a.description || '').toLowerCase().includes(q))
    },
    displayList: {
      get () { return this.filteredList.slice(0, this.perPage || this.filteredList.length) },
      set (val) {
        if (this.isFiltering) return
        this.list = [...val, ...this.list.slice(val.length)]
      }
    }
  },
  methods: {
    clearFilters () { this.search = '' },
    load () {
      const loader = this.$loading.show()
      services.listArchives().then(res => { this.list = res.data.data }).catch(() => errorMessage('Falha ao carregar arquivos.', 3000)).finally(() => loader.hide())
    },
    openCreate () { this.form = empty(); this.showModal = true },
    openEdit (a) { this.form = { ...a }; this.showModal = true },
    async save () {
      this.saving = true
      try {
        const fd = new FormData()
        fd.append('description', this.form.description)
        fd.append('url_download', this.form.url_download || '')
        if (this.form.id) await services.updateArchive(this.form.id, fd)
        else await services.createArchive(fd)
        this.showModal = false; successMessage('Arquivo salvo.', 2000); this.load()
      } catch (e) { errorMessage(e?.response?.data?.message || 'Falha ao salvar arquivo.', 3000) } finally { this.saving = false }
    },
    async toggle (a) { try { await services.changeArchiveStatus(a.id); a.status = a.status ? 0 : 1 } catch (e) { errorMessage('Falha ao alterar status.', 3000) } },
    async remove (a) {
      const ok = await this.$bvModal.msgBoxConfirm(`Remover "${a.description}"?`, { okTitle: 'Remover', cancelTitle: 'Cancelar', okVariant: 'danger' })
      if (!ok) return
      try { await services.deleteArchive(a.id); this.load() } catch (e) { errorMessage('Falha ao remover.', 3000) }
    },
    async onReorder () {
      try {
        await services.reorderArchives(this.list.map(it => ({ id: it.id })))
      } catch (e) { errorMessage('Falha ao reordenar.', 3000); this.load() }
    }
  }
}
</script>
